import { motion } from 'framer-motion';
import { Stethoscope, Wallet, Eye, Crown, Shield, Globe2 } from 'lucide-react';
import { SceneFrame, Pill } from './_atoms';

const standard = [
  { Icon: Stethoscope, label: 'Provider',   desc: 'Entities that deliver healthcare services and submit claims.',      color: 'from-teal-500 to-teal-700',     border: 'border-teal-400' },
  { Icon: Wallet,      label: 'Payer',      desc: 'Entities that process and adjudicate claims.',                       color: 'from-amber-500 to-amber-700',   border: 'border-amber-400' },
];

const crossEntity = [
  { Icon: Eye,    label: 'Regulator', desc: 'Oversight entities with read-level access across all organizations.', color: 'from-indigo-500 to-indigo-700',  border: 'border-indigo-400' },
  { Icon: Crown,  label: 'Owner',     desc: 'Platform ownership — full visibility and control across all entities.',  color: 'from-rose-500 to-rose-700',      border: 'border-rose-400' },
];

function CategoryCard({ Icon, label, desc, color, border, delay, scope }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-2xl bg-white border-2 ${border} p-5 shadow-sm`}
    >
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-3 shadow-md`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-heading font-extrabold text-[16px] text-[#0A0A0A]">{label}</h3>
        {scope === 'standard' && <Pill tone="slate">Own entities only</Pill>}
        {scope === 'cross' && <Pill tone="indigo">All entities</Pill>}
      </div>
      <p className="font-body text-[12px] text-[#6B7280] leading-snug">{desc}</p>
    </motion.div>
  );
}

export default function ArchCategoriesScene() {
  return (
    <SceneFrame
      eyebrow="Entity categories"
      title="The single source of truth"
      subtitle="Entity Category governs module behavior, business logic and the role catalog. Categories split into two groups with very different access scopes."
    >
      {/* Standard */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
        <div className="flex items-center gap-2 mb-3 mt-2">
          <Shield size={15} className="text-slate-500" />
          <h4 className="font-heading font-bold text-[13px] text-[#374151] uppercase tracking-wider">Standard categories — tenant-scoped</h4>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {standard.map((c, i) => <CategoryCard key={c.label} {...c} scope="standard" delay={0.35 + i * 0.15} />)}
        </div>
      </motion.div>

      {/* Cross-entity */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <Globe2 size={15} className="text-indigo-500" />
          <h4 className="font-heading font-bold text-[13px] text-[#374151] uppercase tracking-wider">Cross-entity categories — system-wide</h4>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {crossEntity.map((c, i) => <CategoryCard key={c.label} {...c} scope="cross" delay={0.95 + i * 0.15} />)}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6 }}
        className="mt-6 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-start gap-2.5"
      >
        <Shield size={14} className="text-slate-500 flex-shrink-0 mt-0.5" />
        <p className="text-[12px] font-body text-slate-700 leading-snug">
          <span className="font-bold">Cross-entity categories are distinct entity types</span> — not elevated roles inside standard categories.
          This prevents any single user from holding both operational <em>and</em> oversight access simultaneously (NIST AC-5 separation of duties).
        </p>
      </motion.div>
    </SceneFrame>
  );
}
